import { GraphQLClient } from 'graphql-request';
import type { BackendErrorResponse } from './@types';
import { acquireAuthToken } from './utils/auth-configs';
import {
  checkEmulationMode,
  EmulateHeaders,
  EmulationMode,
} from './utils/emulate';

function getOperationName(body: unknown): string | undefined {
  if (typeof body !== 'string') return;
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.query !== 'string') return;
    if (parsed.operationName) return parsed.operationName;
    const match = parsed.query.match(/(?:query|mutation|subscription)\s+(\w+)/);
    return match?.[1];
  } catch {
    return undefined;
  }
}

export const getEndpoint = () => {
  if (process.env.PUBLIC_REACT_APP_GRAPHQL_API_URI) {
    return process.env.PUBLIC_REACT_APP_GRAPHQL_API_URI;
  }

  const { origin } = window.location;
  const url = new URL(origin);
  const parts = url.hostname.split('.');

  // Preview environments use nginx proxy (same origin)
  if (url.hostname.endsWith('.ts.net')) {
    return `${origin}/api/tyrogql`;
  }

  // If we have a subdomain, replace it with 'api'
  if (parts.length > 2) {
    parts[0] = 'api';
    url.hostname = parts.join('.');
    return `${url.origin}/api/tyrogql`;
  }

  // For localhost or no subdomain
  return `${origin}/api/tyrogql`;
};

type FetchArguments = Parameters<typeof fetch>;

const getAuthHeaders = async (...args: FetchArguments) => {
  const headers = new Headers(args[1]?.headers);
  const emulationMode = checkEmulationMode();

  // get the authentication token from ApplicationSettings if it exists
  const token = await acquireAuthToken();
  if (typeof token === 'string') {
    headers.set('authorization', `Bearer ${token}`);
  }

  // headers['X-THROW-HTTP-ERROR'] = '401';

  // add the emulate custom header to the headers
  const tenantId = localStorage.getItem(EmulateHeaders.TENANT);
  const partyId = localStorage.getItem(EmulateHeaders.PARTY_ID);
  const guestTenantId = localStorage.getItem(EmulateHeaders.GUEST_TENANT);
  if (emulationMode === EmulationMode.Tenant && typeof tenantId === 'string') {
    headers.set(EmulateHeaders.TENANT, tenantId);
  } else if (guestTenantId) {
    headers.set(EmulateHeaders.GUEST_TENANT, guestTenantId);
  }

  if (
    emulationMode === EmulationMode.User &&
    typeof tenantId === 'string' &&
    typeof partyId === 'string'
  ) {
    headers.set(EmulateHeaders.TENANT, tenantId);
    headers.set(EmulateHeaders.PARTY_ID, partyId);
  }

  const academicNamespaceId = localStorage.getItem(
    EmulateHeaders.ACADEMIC_NAMESPACE_ID,
  );

  if (
    !headers.has(EmulateHeaders.ACADEMIC_NAMESPACE_ID) &&
    typeof academicNamespaceId === 'string'
  ) {
    headers.set(EmulateHeaders.ACADEMIC_NAMESPACE_ID, academicNamespaceId);
  }

  args[1] = {
    ...args[1],
    headers: Object.fromEntries(headers.entries()),
  };

  return args;
};

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const GRAPHQL_OPERATION_KEYWORD = /\b(query|mutation|subscription)\b/;
const MAX_NETWORK_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 150;

// fetch() rejects with a TypeError on network-level failures — including the
// dropped pooled connections Safari surfaces as "TypeError: Load failed" but
// Chrome replays transparently.
function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError;
}

// Only reads are safe to replay: a named `query`, or the anonymous `{ ... }`
// shorthand. The keyword match skips any leading fragment definitions, so a
// mutation that defines fragments first is still treated as non-replayable.
function isReplayableGraphqlOperation(body: string): boolean {
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.query !== 'string') return false;
    const keyword = parsed.query.match(GRAPHQL_OPERATION_KEYWORD)?.[1];
    return keyword === undefined || keyword === 'query';
  } catch {
    return false;
  }
}

// Replaying a non-idempotent request risks double-submitting if the connection
// dropped after the server processed it — e.g. paying fees twice.
function isReplayableRequest(args: FetchArguments): boolean {
  const init = args[1];
  const body = init?.body;
  if (typeof body === 'string' && body.startsWith('{"query":')) {
    return isReplayableGraphqlOperation(body);
  }
  const method = (init?.method ?? 'GET').toUpperCase();
  return IDEMPOTENT_METHODS.has(method);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithNetworkRetry(args: FetchArguments): Promise<Response> {
  const replayable = isReplayableRequest(args);
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fetch(...args);
    } catch (error) {
      const canRetry =
        replayable && isNetworkError(error) && attempt < MAX_NETWORK_RETRIES;
      if (!canRetry) throw error;
      await delay(RETRY_BASE_DELAY_MS * 2 ** attempt);
    }
  }
}

export async function fetchInstance(...args: FetchArguments) {
  const patchedArgs = await getAuthHeaders(...args);
  const operationName = getOperationName(patchedArgs[1]?.body);
  const response = await fetchWithNetworkRetry(patchedArgs);

  if (operationName) {
    import('@sentry/react').then((Sentry) => {
      Sentry.addBreadcrumb({
        category: 'graphql',
        message: operationName,
        data: {
          status_code: response.status,
          method: 'POST',
        },
        level: response.ok ? 'info' : 'error',
      });
    });
  }

  // Try to acquire a new token if the response is 401
  if (response?.status === 401) {
    const newToken = await acquireAuthToken();

    if (newToken) {
      return fetchWithNetworkRetry([
        patchedArgs[0],
        {
          ...patchedArgs[1],
          headers: {
            ...patchedArgs[1]?.headers,
            authorization: `Bearer ${newToken}`,
          },
        },
      ]);
    }
  }

  if (
    !response.ok &&
    typeof patchedArgs[1]?.body === 'string' &&
    patchedArgs[1].body.startsWith('{"query":')
  ) {
    const body = await response.json();
    return await Promise.reject({
      response: {
        error: body,
        status: response.status,
        headers: response.headers,
      },
      request: {
        query: patchedArgs[1]?.body,
        variables: (
          patchedArgs[1] as RequestInit & {
            variables?: Record<string, unknown>;
          }
        )?.variables,
        operationName,
      },
    } satisfies BackendErrorResponse);
  }

  return response;
}

type BodyType = keyof Omit<Body, 'body' | 'bodyUsed'>;

export async function fetchClient<TResponse = unknown>(
  url: string,
  options?: RequestInit & { bodyType: BodyType },
) {
  const bodyType: BodyType = options?.bodyType || 'json';

  try {
    const endpoint = new URL(url, getEndpoint());
    const response = await fetchInstance(endpoint, {
      ...options,
    });

    let returnedResponse: Response | unknown = response;

    if (!response.ok) {
      if (bodyType === 'json') {
        returnedResponse = (await response[bodyType]()) as unknown;
      }
      return await Promise.reject(returnedResponse);
    }

    if (bodyType === 'json') {
      returnedResponse = (await response[bodyType]()) as TResponse;
    }

    return returnedResponse as TResponse;
  } catch (error) {
    return Promise.reject(error);
  }
}

export const gqlClient = new GraphQLClient(getEndpoint(), {
  fetch: fetchInstance,
});
